/**
 * Announcement bar countdown (isolated from wbcustom.js / .countd_all)
 * End time in theme settings is interpreted as Beijing time (UTC+8).
 * Expired slides are removed from the DOM so the theme slideshow keeps working.
 */
(function () {
  const SELECTOR = '[data-announcement-countdown]';
  const BEIJING_OFFSET_MS = 8 * 60 * 60 * 1000;

  function parseEndDateBeijing(dateStr) {
    if (!dateStr) return NaN;
    const trimmed = dateStr.trim();
    const match = trimmed.match(
      /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/
    );
    if (!match) return NaN;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    const hour = match[4] !== undefined ? parseInt(match[4], 10) : 23;
    const minute = match[5] !== undefined ? parseInt(match[5], 10) : 59;
    const second = match[6] !== undefined ? parseInt(match[6], 10) : 59;

    if (month < 1 || month > 12 || day < 1 || day > 31) return NaN;

    return Date.UTC(year, month - 1, day, hour, minute, second) - BEIJING_OFFSET_MS;
  }

  function pad2(value) {
    return String(Math.max(0, Math.floor(value))).padStart(2, '0');
  }

  /** Update text without replacing child nodes (avoids childList mutations that trigger PushOwl cart.json). */
  function setCountdownText(el, value) {
    let textNode = el._geekoCountdownTextNode;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      el.textContent = value;
      textNode = el.firstChild;
      el._geekoCountdownTextNode = textNode;
      return;
    }
    if (textNode.data !== value) textNode.data = value;
  }

  function getSlide(countdownRoot) {
    return countdownRoot ? countdownRoot.closest('.slideshow__slide') : null;
  }

  function updateAnnounceHeight() {
    const section = document.querySelector('.announcement-bar-section');
    const outer = section?.querySelector('.announcement--bar');
    let height = 0;
    if (outer && !outer.classList.contains('announcement--bar--all-expired') && outer.style.display !== 'none') {
      height = outer.offsetHeight;
    }
    document.documentElement.style.setProperty('--announce-height', `${height}px`);
  }

  function resetSlideshow(slideshow) {
    if (!slideshow || !slideshow.slider) return;

    if (typeof slideshow.resetPages === 'function') {
      slideshow.resetPages();
    }

    slideshow.sliderItemsToShow = slideshow.slider.querySelectorAll('.slideshow__slide');
    slideshow.sliderFirstItemNode = slideshow.slider.querySelector('.slideshow__slide');

    if (typeof slideshow.isElementVisible === 'function' && slideshow.sliderItemsToShow.length) {
      slideshow.extraVisibleElement = 0;
      slideshow.sliderItemsToShow.forEach((ele) => {
        if (slideshow.isElementVisible(ele)) slideshow.extraVisibleElement++;
      });
      if (slideshow.extraVisibleElement !== 0) slideshow.extraVisibleElement--;
    }

    if (slideshow.slider.getAttribute('data-autoplay') === 'true' && typeof slideshow.play === 'function') {
      slideshow.play();
    }
  }

  function removeExpiredSlide(slide) {
    if (!slide || slide.hasAttribute('data-announcement-removed')) return;

    const section = slide.closest('.announcement-bar-section');
    slide.setAttribute('data-announcement-removed', 'true');
    slide.remove();

    const slideshow = section?.querySelector('slideshow-component');
    resetSlideshow(slideshow);
    updateSectionLayout(section);
  }

  function updateSectionLayout(section) {
    if (!section) return;

    const outerBar = section.querySelector('.announcement--bar');
    const announceText = section.querySelector('.announce_item.announce__text');
    const remainingSlides = section.querySelectorAll('.slideshow__slide');

    if (announceText) {
      if (remainingSlides.length === 0) {
        announceText.classList.add('announce__text--collapsed');
        announceText.setAttribute('hidden', '');
        announceText.style.display = 'none';
      } else {
        announceText.classList.remove('announce__text--collapsed');
        announceText.removeAttribute('hidden');
        announceText.style.display = '';
      }
    }

    if (outerBar) {
      if (remainingSlides.length === 0) {
        outerBar.classList.add('announcement--bar--all-expired');
        outerBar.setAttribute('hidden', '');
        outerBar.style.display = 'none';
      } else {
        outerBar.classList.remove('announcement--bar--all-expired');
        outerBar.removeAttribute('hidden');
        outerBar.style.display = '';
      }
    }

    section.classList.add('announcement-countdown-ready');
    updateAnnounceHeight();
  }

  function tick() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.hasAttribute('data-announcement-expired')) return;

      const hoursEl = root.querySelector('.wb_chours');
      const minutesEl = root.querySelector('.wb_cminutes');
      const secondsEl = root.querySelector('.wb_cseconds');
      if (!hoursEl || !minutesEl || !secondsEl) return;

      const endTime = parseEndDateBeijing(root.getAttribute('data-date'));
      let remaining = endTime - Date.now();

      if (Number.isNaN(endTime) || remaining < 0) {
        root.setAttribute('data-announcement-expired', 'true');
        removeExpiredSlide(getSlide(root));
        return;
      }

      const bar = root.closest('.announcement-bar');
      if (bar) {
        bar.classList.add('announcement-bar--countdown-active');
      }

      const cycleHours = parseInt(root.getAttribute('geeko-cycle-hour') || '0', 10);
      if (!Number.isNaN(cycleHours) && cycleHours > 0) {
        const cycleMs = cycleHours * 60 * 60 * 1000;
        remaining = remaining % cycleMs;
        if (remaining === 0) remaining = cycleMs;
      }

      const totalHours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      setCountdownText(hoursEl, pad2(totalHours));
      setCountdownText(minutesEl, pad2(minutes));
      setCountdownText(secondsEl, pad2(seconds));
    });
  }

  function init() {
    tick();
    document.querySelectorAll('.announcement-bar-section').forEach(updateSectionLayout);
    if (!window.__announcementBarCountdownInterval) {
      window.__announcementBarCountdownInterval = setInterval(tick, 1000);
    }
  }

  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
